import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { AlertTriangle, Loader2, ShieldAlert, X } from "lucide-react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";

const FIELDS = ["format", "recipient_count", "pinned_version", "bing_profile"];

/** Mandatory final safeguard before a real postcard batch reaches Bing.
 *  Four values, four separate confirmations, single-use server token. */
export default function PostcardLiveSendConfirm({ campaignId, kind = "batch", inviteId, onClose, onSent }) {
  const [state, setState] = useState(null);
  const [checks, setChecks] = useState({});
  const [busy, setBusy] = useState(false);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get(
        `/admin/property-campaigns/${campaignId}/live-send-confirmation`,
        { params: { kind, ...(inviteId ? { invite_id: inviteId } : {}) } });
      setState((prev) => {
        // any change to the reviewed state voids the confirmations
        if (prev && prev.fingerprint !== data.fingerprint) setChecks({});
        return data;
      });
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not build the LIVE SEND confirmation");
      onClose?.();
    } finally { setLoading(false); }
  }, [campaignId, kind, inviteId, onClose]);

  useEffect(() => { setChecks({}); load(); }, [load]);

  const allChecked = FIELDS.every((f) => checks[f]);
  const ready = !!state?.can_send && allChecked && !busy;
  const count = state?.recipient_count ?? 0;

  const send = async () => {
    setBusy(true);
    try {
      const { data: conf } = await api.post(
        `/admin/property-campaigns/${campaignId}/live-send-confirmation/confirm`, {
          kind,
          ...(inviteId ? { invite_id: inviteId } : {}),
          confirm_format: state.format,
          confirm_recipient_count: state.recipient_count,
          confirm_pinned_version: state.pinned_version?.label,
          confirm_bing_profile: state.bing_profile,
          fingerprint: state.fingerprint,
        });
      const body = { confirmation_id: conf.confirmation_id, confirm_live: true };
      if (kind === "single") {
        await api.post(`/admin/property-campaigns/${campaignId}/mailout/resend/${inviteId}`, body);
        toast.success("1 LIVE postcard submitted to Bing");
      } else {
        const { data: r } = await api.post(
          `/admin/property-campaigns/${campaignId}/mailout/send`,
          { ...body, confirm_count: state.recipient_count });
        if (r.failed > 0) toast.warning(`${r.submitted} submitted, ${r.failed} failed`);
        else toast.success(`${r.submitted} LIVE postcards submitted to Bing`);
      }
      onSent?.();
      onClose?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || "LIVE send blocked", { duration: 12000 });
      setChecks({});      // single use — everything must be re-confirmed
      await load();
    } finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-[60] bg-slate-950/80 overflow-y-auto p-4 sm:p-8" data-testid="pc-live-send-screen">
      <div className="mx-auto max-w-3xl rounded-2xl bg-white shadow-2xl overflow-hidden">
        <div className="flex items-center gap-3 bg-rose-600 px-5 py-4 text-white">
          <ShieldAlert className="w-6 h-6 shrink-0" />
          <h2 className="text-base md:text-lg font-black uppercase tracking-wider" data-testid="pc-live-send-title">
            Live send confirmation
          </h2>
          <button onClick={onClose} data-testid="pc-live-send-close" className="ml-auto p-1 rounded-full hover:bg-white/20">
            <X className="w-5 h-5" />
          </button>
        </div>

        {loading && !state ? (
          <div className="p-10 flex justify-center"><Loader2 className="w-6 h-6 animate-spin text-slate-400" /></div>
        ) : !state ? null : (
          <div className="p-5 sm:p-6">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3" data-testid="pc-live-send-values">
              <Value tid="format" label="Format" value={state.format} />
              <Value tid="recipients" label="Recipients" value={String(count)} />
              <Value tid="pinned" label="Pinned Version" value={state.pinned_version?.label || "—"}
                sub={state.pinned_version?.template_version_id
                  ? `Version ID ${state.pinned_version.template_version_id}`
                  : state.pinned_version?.kind === "legacy" ? "Immutable code-generated artwork" : null} />
              <Value tid="profile" label="Bing Profile" value={state.bing_profile || "—"} />
            </div>

            {state.blockers?.length > 0 && (
              <div className="mt-5 rounded-xl border-2 border-rose-300 bg-rose-50 p-4" data-testid="pc-live-send-blockers">
                <div className="flex items-center gap-2 text-sm font-black uppercase tracking-wider text-rose-800">
                  <AlertTriangle className="w-4 h-4" /> Live send blocked
                </div>
                <ul className="mt-2 space-y-1.5 text-sm text-rose-900">
                  {state.blockers.map((b, i) => (
                    <li key={i} data-testid={`pc-live-send-blocker-${i}`}>• {b}</li>
                  ))}
                </ul>
              </div>
            )}

            <div className="mt-5 space-y-2.5" data-testid="pc-live-send-checks">
              {(state.confirmations || []).map((c) => (
                <label key={c.key} data-testid={`pc-live-send-check-${c.key}-label`}
                  className={`flex items-start gap-3 rounded-xl border p-3 cursor-pointer transition-colors ${checks[c.key] ? "border-emerald-300 bg-emerald-50" : "border-slate-200 bg-slate-50 hover:bg-slate-100"}`}>
                  <input type="checkbox" data-testid={`pc-live-send-check-${c.key}`}
                    checked={!!checks[c.key]} disabled={busy || !state.can_send}
                    onChange={(e) => setChecks((p) => ({ ...p, [c.key]: e.target.checked }))}
                    className="mt-0.5 h-4 w-4 accent-emerald-600" />
                  <span className="text-sm font-semibold text-slate-800">{c.label}</span>
                </label>
              ))}
            </div>

            <div className="mt-5 rounded-xl bg-slate-900 px-4 py-3 text-sm font-bold text-white" data-testid="pc-live-send-warning">
              {state.warning_text}
            </div>

            <div className="mt-4 flex flex-col sm:flex-row-reverse gap-2">
              <Button data-testid="pc-live-send-button" disabled={!ready} onClick={send}
                className="rounded-full bg-rose-600 hover:bg-rose-700 disabled:opacity-40 font-black tracking-wider px-6">
                {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : (state.button_label || `SEND ${count} LIVE POSTCARDS`)}
              </Button>
              <Button data-testid="pc-live-send-cancel" variant="outline" className="rounded-full" onClick={onClose} disabled={busy}>
                Cancel
              </Button>
              <Button data-testid="pc-live-send-revalidate" variant="ghost" className="rounded-full sm:mr-auto text-xs" onClick={load} disabled={busy}>
                Re-check campaign
              </Button>
            </div>

            <p className="mt-3 text-[11px] text-slate-500" data-testid="pc-live-send-footnote">
              This confirmation is single use and is bound to the exact state shown above
              (fingerprint <span className="font-mono">{String(state.fingerprint).slice(0, 12)}</span>).
              Everything is revalidated on the server immediately before the Bing call — if
              the recipient count, pinned version, Bing profile or any generated artwork has
              changed, the send is cancelled and you must confirm again.
            </p>
          </div>
        )}
      </div>
    </div>
  );
}

const Value = ({ tid, label, value, sub }) => (
  <div className="rounded-xl border border-slate-200 bg-white p-3.5" data-testid={`pc-live-send-value-${tid}`}>
    <div className="text-[11px] font-bold uppercase tracking-widest text-slate-500">{label}</div>
    <div className="mt-1 text-lg font-black text-slate-900 break-words leading-tight">{value}</div>
    {sub && <div className="mt-0.5 text-[10px] font-mono text-slate-400 break-all">{sub}</div>}
  </div>
);
